import React, { useState } from "react";
import styled from "@emotion/styled";
import _ from "lodash";
import { Flex, Text } from "styles/utility-components";

import CategoryDropdown from "components/form_components/dropdown_list_selects/new_supplier_selects/CategoryDropdown";
import InfiniteSuppliersChecklist from "components/form_components/dropdown_list_selects/new_supplier_selects/InfiniteSuppliersChecklist";
import SignalSuppliersChecklist from "components/form_components/dropdown_list_selects/new_supplier_selects/SignalSupplierChecklist";
import { SupplierCheckbox } from "components/form_components/dropdown_list_selects/new_supplier_selects/SupplierCheckbox";
import {
  SupplierOption,
  SupplierSelectCategory,
} from "components/form_components/dropdown_list_selects/new_supplier_selects/types";
import Divider from "components/ui/divider/Divider";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { Organisation } from "lib/generated/app-service-gql/graphql";

type OrgOption = Organisation & { tagLabel?: string };

type Props = {
  open?: boolean;
  competitors: OrgOption[];
  partners: OrgOption[];
  isLoading: boolean;
  handleSuppliersSelection: (suppliers: SupplierOption[]) => void;
  textSearch: string;
  selectedSuppliers: SupplierOption[];
  categoryOptions?: SupplierSelectCategory[];
};

function SimpleSuppliersDropdown({
  competitors,
  partners,
  isLoading,
  handleSuppliersSelection,
  textSearch,
  selectedSuppliers,
  categoryOptions = ["Competitor", "Partner"],
}: Props) {
  const [categorySelected, setCategorySelected] = useState<SupplierSelectCategory>();

  const signalSupplierOptions = categorySelected === "Competitor" ? competitors : partners;

  const allSignalSuppliers = _.union(competitors, partners).map((s) => ({
    label: s.name,
    value: s.id,
  }));

  function handleCategorySelection(label: SupplierSelectCategory) {
    setCategorySelected(label);
  }

  function getCategoryLabel(category?: string): string {
    switch (category) {
      case "Competitor":
        return `View competitors (${competitors.length})`;
      case "Partner":
        return `View partners (${partners.length})`;
      default:
        return `View partners (${partners.length})`;
    }
  }

  const selectedSupplierIds = selectedSuppliers.map((s) => s.value);

  const renderedCategoryInput = () => {
    if (isLoading) return <CentredSpinner />;

    if (categorySelected === undefined && textSearch.length === 0)
      return (
        <Container>
          <CategoryDropdown
            categories={categoryOptions}
            handleCategorySelection={handleCategorySelection}
            getCategoryLabel={getCategoryLabel}
            showSignalIcon={false}
          />
          <Divider />
          <Label color="sysTextSecondary">Selected</Label>
          {selectedSuppliers.map((supplier) => {
            return (
              <SupplierCheckbox
                key={supplier.value}
                supplier={{
                  label: supplier.label,
                  value: supplier.value,
                  tagLabel: supplier.tagLabel,
                }}
                selectedSupplierIds={selectedSupplierIds}
                onChange={(isChecked) => {
                  if (isChecked) {
                    handleSuppliersSelection([
                      ...selectedSuppliers,
                      { label: supplier.label, value: supplier.value, tagLabel: supplier.tagLabel },
                    ]);
                  } else {
                    handleSuppliersSelection(
                      selectedSuppliers.filter((s) => s.value !== supplier.value),
                    );
                  }
                }}
                showSignalIcon={false}
                showTooltip={false}
                showSupplierCountry={false}
              />
            );
          })}
        </Container>
      );

    if (textSearch.length > 0) {
      return (
        <InfiniteSuppliersChecklist
          signalSuppliers={allSignalSuppliers}
          excludeSignals={textSearch.length === 0}
          textSearch={textSearch}
          onChange={handleSuppliersSelection}
          selectedSuppliers={selectedSuppliers}
          showSignalIcon={false}
          showSupplierCountry={false}
          showTooltip={false}
        />
      );
    }

    if (categorySelected === "Other suppliers") {
      return null;
    }

    return (
      <SignalSuppliersChecklist
        options={signalSupplierOptions}
        category={categorySelected ?? "Partner"}
        onChange={handleSuppliersSelection}
        selectedSuppliers={selectedSuppliers}
        backBtnFn={() => setCategorySelected(undefined)}
        showSignalIcon={false}
        showSupplierCountry={false}
        showTooltip={false}
        backBtnStyle="compact"
      />
    );
  };

  return renderedCategoryInput();
}

const Container = styled(Flex)({
  flexDirection: "column",
  maxHeight: "320px",
});

const Label = styled(Text)({
  padding: 4,
});

export default SimpleSuppliersDropdown;
